import { type FC, type ReactNode, useEffect, useMemo } from "react";
import { matchPath, useLocation, useNavigate } from "react-router";

import { type ErrorType } from "@vega-api/configs";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { DefaultErrorPanel } from "@vega-core/components/Cards/DefaultErrorPanel/DefaultErrorPanel";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { arrayToString } from "@vega-core/utils/formatArray";

import { type UiCapabilityKey, useUiCapabilities } from "./useUiCapabilities";

export type CapabilityPathMap = Partial<Record<UiCapabilityKey, string[]>>;

type RouteProtectionWrapperProps = {
  children: ReactNode;
  fallbackPath?: string;
  capabilityPathMap?: CapabilityPathMap;
};

const getKeysGuardingPath = (
  currentPath: string,
  pathMap: Record<string, string[] | undefined>,
): string[] =>
  Object.keys(pathMap).filter((key) =>
    (pathMap[key] ?? []).some(
      (allowedPath) => !!matchPath(allowedPath, currentPath),
    ),
  );

const anySatisfied = (
  requiredKeys: string[],
  flags: Record<string, boolean>,
): boolean => requiredKeys.some((key) => !!flags[key]);

const getMissingKeys = (
  requiredKeys: string[],
  flags: Record<string, boolean>,
): string[] => requiredKeys.filter((key) => !flags[key]);

export const RouteProtectionWrapper: FC<RouteProtectionWrapperProps> = ({
  children,
  fallbackPath = "/",
  capabilityPathMap = {},
}) => {
  const { capabilities, isLoading, error } = useUiCapabilities();

  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const requiredCapabilities = useMemo(
    () => getKeysGuardingPath(location.pathname, capabilityPathMap),
    [location.pathname, capabilityPathMap],
  );

  const hasRequirements = requiredCapabilities.length > 0;

  const hasSufficientCapabilities = useMemo(() => {
    if (isLoading || error) return false;

    if (requiredCapabilities.length === 0) {
      return true;
    }

    return anySatisfied(requiredCapabilities, capabilities);
  }, [isLoading, error, requiredCapabilities, capabilities]);

  useEffect(() => {
    if (!isLoading && !error && !hasSufficientCapabilities && hasRequirements) {
      const missing = getMissingKeys(requiredCapabilities, capabilities);

      toast({
        title: m.helpCentre_permissions_noEditAccess(),
        description: m.common_errors_missingPermissions({
          permissions: arrayToString(missing),
        }),
        variant: "destructive",
        toastId: "permissions-guard",
      });

      navigate(fallbackPath, { state: { from: location }, replace: true });
    }
  }, [
    isLoading,
    error,
    hasSufficientCapabilities,
    hasRequirements,
    requiredCapabilities,
    capabilities,
    navigate,
    fallbackPath,
    location,
    toast,
  ]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    const { description } = getErrorMessage(error as ErrorType<unknown>, {
      defaultMessage: m.common_error_failedToFetchPermission(),
    });

    toast({
      title: m.common_error_failedToFetchPermission(),
      description,
      variant: "destructive",
      toastId: "fetch-permission-error",
    });

    return (
      <DefaultErrorPanel
        description={description}
        title={m.common_error_failedToFetchPermission()}
      />
    );
  }

  if (!hasSufficientCapabilities && hasRequirements) {
    return null;
  }

  return children;
};
